import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import {
  TimePicker,
  formatDateToTimeString,
  type TimeSelectionData,
  type TimeSelectionEvents,
} from '@fluentui/react-timepicker-compat';
import { formatDate, isValidDate } from '@moko/utils';
import { useIsMobile } from '../hooks/useIsMobile';

const withDate = (current: Date | null, nextDate: Date) => {
  const timeSource = current ?? new Date();
  const next = new Date(nextDate);
  next.setHours(timeSource.getHours(), timeSource.getMinutes(), 0, 0);
  return next;
};

const withTime = (current: Date | null, nextTime: Date) => {
  const next = new Date(current ?? Date.now());
  next.setHours(nextTime.getHours(), nextTime.getMinutes(), 0, 0);
  return next;
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    width: '100%',
    overflow: 'hidden',
  },
  stacked: {
    flexDirection: 'column',
  },
  part: {
    minWidth: 0,
  },
  datePart: {
    flex: 3,
  },
  timePart: {
    flex: 2,
  },
});

const dateTimeOverrides = `
  .fui-DateTimeSelect .fui-DatePicker,
  .fui-DateTimeSelect .fui-TimePicker {
    min-width: 0 !important;
    width: 100% !important;
  }
`;

export type DateTimeSelectProps = {
  value?: Date | null;
  onChange: (value: Date | null) => void;
  disabled?: boolean;
  hideTime?: boolean;
  minDate?: Date;
  className?: string;
};

export const DateTimeSelect = ({
  value,
  onChange,
  disabled,
  hideTime,
  minDate,
  className,
}: DateTimeSelectProps) => {
  const isMobile = useIsMobile();
  const styles = useStyles();
  const current = isValidDate(value) ? value : null;

  return (
    <>
      <style>{dateTimeOverrides}</style>
      <div
        className={mergeClasses(
          'fui-DateTimeSelect',
          styles.root,
          isMobile && styles.stacked,
          className
        )}
      >
        <div
          className={mergeClasses(styles.part, !isMobile && styles.datePart)}
        >
          <DatePicker
            style={{ width: '100%' }}
            minDate={minDate}
            value={current ?? undefined}
            formatDate={(date) => (date ? formatDate(date) : '')}
            onSelectDate={(selected) => {
              if (!selected) {
                onChange(null);
                return;
              }
              onChange(withDate(current, selected));
            }}
            disabled={disabled}
          />
        </div>
        {!hideTime && (
          <div
            className={mergeClasses(styles.part, !isMobile && styles.timePart)}
          >
            <TimePicker
              selectedTime={current}
              value={current ? formatDateToTimeString(current) : ''}
              freeform
              dateAnchor={current ?? undefined}
              onTimeChange={(
                _event: TimeSelectionEvents,
                data: TimeSelectionData
              ) => {
                if (!data.selectedTime) return;
                onChange(withTime(current, data.selectedTime));
              }}
              disabled={disabled}
            />
          </div>
        )}
      </div>
    </>
  );
};
